import axios, { AxiosResponse, AxiosError } from 'axios';
import { navigate } from 'gatsby-link';
import { IFacility, FacilityOpenDates } from '../models';
import FacilityDetails, {
    FacilityWaitlist,
    FacilityRepairCapacity,
    FacilityRepairCapacityFormValues,
    FacilityCloseDate,
    FacilityDropoffTimeSetting,
    FacilityWaitlistFormValues,
    FacilityCapacityMetricForAWeek as FacilityCapacityMetricForAWeek,
    FacilityCapacityCopyRequest,
    FacilityLeadTime,
} from '../models/IFacility';
import { WaitlistStatics } from '../models/IWaitlist';
import { IBookingList, IBookingSearchParams, IHALSyncResult } from '../models/IBooking';
import { Store } from '../stores';

axios.defaults.baseURL = process.env.GATSBY_API_BASE_URL;

axios.interceptors.request.use((config) => {
    //clear any previous api call errors, before a request is made to api
    Store.apiErrorStore.clearApiError();
    // TODO: Check token expiry
    const token = Store.userStore.token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

axios.interceptors.response.use(
    async (response) => {
        try {
            return response;
        } catch (error) {
            console.log(error);
            return await Promise.reject(error);
        }
    },
    function (error) {
        // Session expired/invalid - send the user back to login instead of letting the app
        // render with a stale/unauthenticated state (which was surfacing as a generic error page).
        if (error?.response?.status === 401) {
            Store.userStore.resetSession();
            if (typeof window !== 'undefined' && window.location.pathname !== '/') {
                navigate('/', { state: { callbackUrl: window.location.pathname } });
            }
        }

        // Any status codes that falls out+side the range of 2xx cause this function to trigger
        // store error details in Error store and rethrow error with error details in Error store
        Store.apiErrorStore.setApiError(error);
        //return Promise.reject(error);
        return Promise.reject(Store.apiErrorStore.apiError);
    }
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
    get: <T>(url: string) => axios.get<T>(url).then(responseBody),
    post: <T>(url: string, body?: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
};

const tokenEndpoint = process.env.GATSBY_API_GET_TOKEN as string;
const adminLoginEndpoint = process.env.GATSBY_API_ADMIN_LOGIN as string;

const Identity = {
    getToken: (username: string, password: string) =>
        requests.post<string>(`${tokenEndpoint}`, { username: username, password: password }),
    adminLogin: (username: string, password: string) =>
        requests.post<string>(`${adminLoginEndpoint}`, { username: username, password: password }),
};

const Facility = {
    getList: () => requests.get<IFacility[]>('/facilities'),
    getFacility: (facilityId: string) => requests.get<IFacility>(`/facility/${facilityId}`),
    saveFacilityDetails: (facility: FacilityDetails) => requests.put<FacilityDetails>('/facility', facility),
    createFacility: (facility: FacilityDetails) => requests.post<FacilityDetails>('/facility', facility),

    getFacilityRepairCapacity: (facilityId: string, startDateOfWeek?: Date) =>
        requests.get<FacilityRepairCapacity>(
            `/facility/capacityConfig/${facilityId}?forTheWeekStartingOn=${startDateOfWeek}`
        ),

    saveFacilityRepairCapacity: (facilityRepairCapacity: FacilityRepairCapacityFormValues) =>
        requests.post<FacilityRepairCapacityFormValues>('/facility/configureCapacity', facilityRepairCapacity),

    getFacilityOpenDates: (facilityId: string) =>
        requests.get<FacilityOpenDates>(`/facility/openShutDatesConfig/${facilityId}`),
    saveFacilityOpenDates: (facilityOpenDates: FacilityOpenDates) =>
        requests.post<FacilityOpenDates>('/facility/configureOpenShutDates', facilityOpenDates),

    getFacilityWaitlist: (facilityId: string) =>
        requests.get<FacilityWaitlist>(`/facility/waitlistProcessingStateConfig/${facilityId}`),
    saveFacilityWaitlist: (facilityWaitlist: FacilityWaitlistFormValues) =>
        requests.post<FacilityWaitlistFormValues>('/facility/configureWaitlistProcessingState', facilityWaitlist),

    getFacilityCloseDates: (facilityId: string) =>
        requests.get<FacilityCloseDate[]>(`/facility/closeDatesConfig/${facilityId}`),
    saveFacilityCloseDate: (facilityCloseDate: FacilityCloseDate) =>
        requests.post<FacilityCloseDate>('/facility/configureCloseDate', facilityCloseDate),
    deleteFacilityCloseDate: (facilityCloseDate: FacilityCloseDate) =>
        requests.post<FacilityCloseDate>('/facility/deleteCloseDateConfig', facilityCloseDate),

    getFacilityDropoffTimeSetting: (facilityId: string) =>
        requests.get<FacilityDropoffTimeSetting>(`/facility/dropOffTimesConfig/${facilityId}`),
    saveFacilityDropoffTimeSetting: (facilityDropoffTimeSetting: FacilityDropoffTimeSetting) =>
        requests.post<FacilityDropoffTimeSetting>('/facility/configureDropOffTimes', facilityDropoffTimeSetting),

    getFacilityWaitlistStats: (facilityId: string) =>
        requests.get<WaitlistStatics>(`/waitlist/getWaitlistStats?facilityId=${facilityId}`),

    getFacilityCapacityMetrics: (facilityId: string, fortheWeekStartingOn?: Date) => {
        if (fortheWeekStartingOn)
            return requests.get<FacilityCapacityMetricForAWeek[]>(
                `/facility/capacityMetrics/${facilityId}?fortheWeekStartingOn=${fortheWeekStartingOn}`
            );
        else return requests.get<FacilityCapacityMetricForAWeek[]>(`/facility/capacityMetrics/${facilityId}`);
    },

    getWeeksWithoutCapacity: (facilityId: string) =>
        requests.get<Date[]>(`/facility/weeksWithoutCapacity/${facilityId}`),

    copyCapacity: (facilityCapacityCopyRequest: FacilityCapacityCopyRequest) =>
        requests.post(`/facility/copyCapacity/${facilityCapacityCopyRequest.facilityId}`, facilityCapacityCopyRequest),

    getFacilityLeadTime: (facilityId: string) => requests.get<FacilityLeadTime>(`/facility/leadAndCutoffTimeConfig/${facilityId}`),
    saveFacilityLeadTime: (facilityLeadTime: FacilityLeadTime) => requests.post<FacilityLeadTime>(`/facility/configureLeadAndCutoffTime`, facilityLeadTime),

    getFacilityDropoffTimeAllowanceSetting: (facilityId: string) =>
        requests.get<FacilityDropoffTimeSetting>(`/facility/dropoffAllowanceConfig/${facilityId}`)
};

const Booking = {
    search: (params: IBookingSearchParams) => {
        const query = new URLSearchParams();
        if (params.facilityId) query.append('facilityId', params.facilityId);
        if (params.status) query.append('status', params.status);
        if (params.bookedBy) query.append('bookedBy', params.bookedBy);
        if (params.searchText) query.append('searchText', params.searchText);
        if (params.createdFrom) query.append('createdFrom', params.createdFrom);
        if (params.createdTo) query.append('createdTo', params.createdTo);
        if (params.sortBy) query.append('sortBy', params.sortBy);
        query.append('sortDescending', String(params.sortDescending));
        query.append('pageNumber', String(params.pageNumber));
        query.append('pageSize', String(params.pageSize));
        return requests.get<IBookingList>(`/booking/search?${query.toString()}`);
    },

    syncToHAL: (bookingId: string) => requests.post<IHALSyncResult>(`/booking/syncToHAL/${bookingId}`),

    getBookingPageLink: (quoteNumber: string) => requests.get<string>(`/booking/registrationLink/${quoteNumber}`),
};

const agent = {
    Facility,
    Identity,
    Booking,
};

export default agent;
